/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import axios from 'axios';
import { getSessionToken, setSessionToken } from '../utils/cookieService';
import offlineDb from '../utils/offlineDb';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
export const SESSION_HEADER = 'x-session-token';

/**
 * Create a specialized axios instance for capture API calls
 * This client automatically handles session tokens via cookies and headers
 */
const captureClient = axios.create({
  baseURL: `${API_URL}/api/capture`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Request interceptor to add session token and slug headers
 */
captureClient.interceptors.request.use(
  (config) => {
    // Extract token from URL (assuming format: /capture/:token/...)
    const tokenMatch = config.url?.match(/^\/([^/]+)/);
    if (tokenMatch && tokenMatch[1]) {
      const assignmentToken = tokenMatch[1];
      const sessionToken = getSessionToken(assignmentToken);
      
      if (sessionToken) {
        config.headers[SESSION_HEADER] = sessionToken;
      }
    }

    // Inject the subdomain slug so the backend can validate it
    const hostname = window.location.hostname;
    const slug = hostname.includes('.') ? hostname.split('.')[0] : '';
    if (slug) {
      config.headers['x-slug'] = slug;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor to handle session token from responses
 */
captureClient.interceptors.response.use(
  async (response) => {
    // If the response contains a sessionToken, store it in cookie — mirroring
    // whatever real expiry the server sends (sessionExpiresAt) instead of
    // guessing a fixed duration client-side. Also mirror it into the offline
    // queue's own IndexedDB store — unlike the cookie (deliberately capped to
    // expire at the assignment's absolute deadline, see cookieService), this
    // survives past that point so a device replaying queued mutations after
    // expiry can still prove which session it owns. See offlineDb.setSessionCredential.
    if (response.data?.sessionToken) {
      const tokenMatch = response.config.url?.match(/^\/([^/]+)/);
      if (tokenMatch && tokenMatch[1]) {
        const assignmentToken = tokenMatch[1];
        setSessionToken(assignmentToken, response.data.sessionToken, response.data.sessionExpiresAt);
        // Best-effort: IndexedDB can be unavailable (private browsing, quota,
        // disabled storage) — that must never fail the response itself, since
        // the cookie above already covers the live-session case on its own.
        try {
          await offlineDb.setSessionCredential(assignmentToken, response.data.sessionToken);
        } catch (err) {
          console.warn('Failed to persist offline replay credential:', err);
        }
      }
    }

    return response;
  },
  (error) => {
    // Handle session conflict specially - don't treat as regular session error
    if (error.response?.status === 409 && error.response?.data?.sessionConflict) {
      if (error.config?.method !== 'get') {
        // Mid-session conflict (POST upload/start/etc.) — notify Capture.jsx via
        // a window event, carrying the server's message so the modal can show
        // the specific reason (e.g. "still working" vs "hasn't synced").
        window.dispatchEvent(new CustomEvent('capture-session-conflict', { detail: error.response?.data }));
      }
      return Promise.reject(error);
    }

    // Handle session-specific errors
    const errorType = error.response?.data?.type;

    if (errorType) {
      // Map session errors to user-friendly messages
      const sessionErrors = {
        'session_expired': 'Your session has expired due to inactivity. Please reload the page.',
        'session_conflict': 'This capture link is already open on another device. Please close it there first.',
        'session_mismatch': 'This capture link is already open on another device.',
        'session_inactive': 'Your session is no longer active. Please reload the page.',
        'no_session': 'No active session found. Please reload the page.',
        'no_session_record': 'No session record found for this capture link.',
      };

      if (sessionErrors[errorType]) {
        error.sessionError = true;
        error.userMessage = sessionErrors[errorType];
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to check if an error is a session error
 * @param {Error} error - The error to check
 * @returns {boolean} True if the error is session-related
 */
export const isSessionError = (error) => {
  return error?.sessionError === true;
};

/**
 * Helper function to get user-friendly error message
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  return error?.userMessage || error.response?.data?.message || 'An error occurred. Please try again.';
};

export default captureClient;
